import { Component, computed, ViewChild } from '@angular/core';
import { ProductsService } from 'src/app/services/products.service';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { entityExistsValidator } from 'src/app/validators/entity-exists.validator';
import { debounceTime, map, of, Subscription, switchMap, tap } from 'rxjs';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { BtnCellRenderer } from 'src/app/modules/shared/components/btn-cell-renderer/btn-cell-renderer.component';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { TextTooltipRenderer } from 'src/app/modules/shared/components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { baseColDef } from 'src/app/modules/shared/utils';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { RightSidebarContainerComponent } from 'src/app/modules/shared/components/right-sidebar-container/right-sidebar-container.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SearchSortComponent } from 'src/app/modules/shared/components/search-sort/search-sort.component';
import { AgGridWrapperComponent } from 'src/app/modules/shared/components/ag-grid-wrapper/ag-grid-wrapper.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { toSignal } from '@angular/core/rxjs-interop';
import { UsersService } from 'src/app/services/users.service';
import { Role } from 'src/app/models/user/role.model';

@Component({
    selector: 'app-products-list',
    templateUrl: './products-list.component.html',
    styleUrls: ['./products-list.component.scss'],
    standalone: true,
    imports: [
      MatSidenavModule,
      RightSidebarContainerComponent,
      ReactiveFormsModule,
      MatFormFieldModule,
      SearchSortComponent,
      AgGridWrapperComponent,
      MatPaginatorModule,
      MatIconModule,
      MatButtonModule,
      MatInputModule
    ]
})
export class ProductsListComponent {
  @ViewChild('drawer') drawer: MatDrawer;
  isLoading: boolean;
  isEditing = false;
  id: number;
  form: FormGroup = new FormGroup({
    name: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    description: new FormControl('', { validators: [Validators.required] }),
    acronym: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
  });

  searchControl: FormControl = new FormControl('');

  currentSortField = {
    sortValue: 'id',
  };
  currentIsAscending = false;

  sortForm: FormGroup = new FormGroup({
    sortField: new FormControl({
      sortValue: this.currentSortField.sortValue,
    }),
    isAscending: new FormControl(this.currentIsAscending),
  });

  pageSettings: {
    defaultPageSize: number;
    pageSizeOptions: number[];
  } = {
    defaultPageSize: 20,
    pageSizeOptions: [20, 50, 100],
  };

  currentPage: number = 0;
  currentCursorId: number | null = null;
  pageSize: number = this.pageSettings.defaultPageSize;
  currentPageSize: number = 20;

  sortOptions = [
    { label: 'Name', sortValue: 'name' },
    { label: 'Acronym', sortValue: 'acronym'},
  ];

  totalCount: number = 0;
  searchKey: string = '';

  subscriptions: Subscription[] = [];

  public columnDefs: ColDef[] = [
    { ...baseColDef, field: 'name',cellRenderer: TextTooltipRenderer, flex: 2 },
    { ...baseColDef, field: 'acronym',cellRenderer: TextTooltipRenderer, flex: 2 },
    { ...baseColDef, field: 'description',cellRenderer: TextTooltipRenderer, flex: 4 },
  ];
  readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isProvider= computed(() => this.userRole() === Role.Provider);
  readonly isSystemUser= computed(() => this.userRole() === Role.SystemUser);
  readonly isConsultant= computed(() => this.userRole() === Role.Consultant);
  readonly isProviderReadOnly = computed(() => this.userRole() === Role.ProviderReadOnly);
  public rowData: any[];

  constructor(
    private productService: ProductsService,
    private userService: UsersService,
    private deleteDialog: DeleteConfirmationDialogService
  ) {}

  ngOnInit() {
    const searchControlSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => {
          this.currentPage = 0;
          this.currentCursorId = null;
          this.reload();
        }),
      )
      .subscribe();

    this.subscriptions.push(searchControlSubscription);

  }

  openRightSidebar(data: any = null) {
    this.setValidators(data);
    if (data) {
      this.id = data.id;
      this.form.setValue({
        name: data.name,
        description: data.description,
        acronym: data.acronym,
      });

      this.isEditing = true;
    } else {
      this.isEditing = false;
    }
    this.drawer.toggle();
    this.updateValueAndValidity();
  }

  onDelete(data: any) {
    this.deleteDialog
      .openDeleteConfirmation(
        `This product will be deleted'.`,
        'Delete confirmation',
        'Delete'
      )
      .afterClosed()
      .pipe(
        switchMap(dialogClosed => {
          if (dialogClosed)
            return this.productService.delete(data.id);
          else return of(false);
        })
      )
      .subscribe((deleteConfirmation) => {
        if (deleteConfirmation) {
          this.productService.clearCache();
          this.reload();
        }
      });
  }

  close() {
    this.form.reset();
    this.drawer.close();
  }

  onSubmit() {
    if (this.form.valid) {
      if (this.isEditing) {
        this.isLoading = true;
        this.productService
          .update(this.id.toString(), this.form.value)
          .subscribe((data) => {
            this.isLoading = false;
            this.productService.clearCache();
            this.close();
            this.reload();
          });
      } else {
        this.isLoading = true;
        this.productService.add(this.form.value).subscribe((data) => {
          this.isLoading = false;
          this.productService.clearCache();
          this.close();
          this.reload();
        });
      }
    }
  }

  private setValidators(data: any = null) {
    this.form.controls['name'].setAsyncValidators([
      entityExistsValidator(this.productService, data?.name),
    ]);
    this.form.controls['acronym'].setAsyncValidators([
      this.acronymExistsValidator(data?.acronym),
    ]);
  }

  private updateValueAndValidity() {
    this.form.controls['name'].updateValueAndValidity();
    this.form.controls['acronym'].updateValueAndValidity();
  }

  acronymExistsValidator(currentAcronym?: string): AsyncValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return of(null);
      }

      if (currentAcronym && control.value === currentAcronym) {
        return of(null);
      }

      return this.productService
        .acronymExists(control.value)
        .pipe(map((res) => (res?.data ? { acronymExists: res.data } : null)));
    };
  }

  onGridReady(params: GridReadyEvent) {
    this.reload();
    this.adjustColumnVisibility(params.api);
  }

  private adjustColumnVisibility(gridApi: any) {
    if(this.isProvider() || this.isConsultant() || this.isProviderReadOnly()) {
      gridApi.setColumnDefs([...this.columnDefs]);
      return;
    }

    const actionCol = {
      field: 'action',
      width: 80,
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        onEdit: this.openRightSidebar.bind(this),
        onDelete: this.onDelete.bind(this)
      }
    };
    gridApi.setColumnDefs([...this.columnDefs, actionCol]);
  }

  private reload(backward: boolean = false) {
    this.searchKey = this.searchControl.value?.toLowerCase() || '';

    let filters: any = {
      sortColumn: this.currentSortField.sortValue,
      sortOrder: this.currentIsAscending ? 'asc' : 'desc',
      forward: !backward,
      pageSize: this.pageSize,
      searchKey: this.searchKey
    };

    if(this.currentCursorId){
      filters = {...filters, lastId: this.currentCursorId};
    }

    const productSub = this.productService
      .get(filters)
      .pipe(
        switchMap((response: any) => {
          if (!response.isError) {
            return of(response.data);
          } else {
            return of([]);
          }
        })
      )
      .subscribe((data: any) => {
        this.totalCount = data.totalCount;
        this.rowData = data.items;
      });

      this.subscriptions.push(productSub);
  }



    setSortField() {
      const { sortField, isAscending } = this.sortForm.value;
      this.currentIsAscending = isAscending;
      this.currentSortField = sortField;
      this.currentCursorId = null;

      this.reload();
    }

    resetSortField() {
      this.sortForm.setValue({
        sortField: this.currentSortField,
        isAscending: this.currentIsAscending
      })
    }

    handlePageEvent(event: PageEvent) {
      let backward: boolean = false;

      if(this.currentPageSize != event.pageSize) {
        this.currentCursorId = null;
        this.currentPageSize = event.pageSize;
      }
      else if(!event.previousPageIndex || event.previousPageIndex < event.pageIndex){
        this.currentCursorId = this.rowData.length > 0 ? this.rowData[this.rowData.length - 1].id : null;
      }
      else{
        this.currentCursorId = this.rowData[0].id;
        backward = true;
      }
      this.currentPage = event.pageIndex;
      this.pageSize = event.pageSize;
      this.reload(backward);
    }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(s => s.unsubscribe());
  }

}
